import { create } from "zustand";
import {
  collection,
  query,
  orderBy,
  where,
  onSnapshot,
  Unsubscribe,
  doc,
  updateDoc,
  deleteField,
  arrayUnion,
  addDoc,
  serverTimestamp,
  WithFieldValue,
  DocumentReference,
  DocumentSnapshot,
  getDoc,
  getCountFromServer,
  getDocs,
  limit,
  runTransaction,
  type Query,
} from "firebase/firestore";

import {
  db, storage, onSnapshotWithRetry, deleteCharacterFn,
  changeOutfitFn, editPersonaFn,
} from "@um/ui";
import { newId } from "@um/ui";
import { uploadAccountImage } from "@um/ui";
import {
  type Persona,
  type ImageReference,
  type Animal,
  type AnimalDeleteReason,
  CHARACTER_DELETED_PAUSE_REASON,
  USER_RETIRED_DELETE_REASON,
  type PublicAnimal,
  type CrewMember,
  type DeleteCharacterResponse,
  AnimalAgent,
  getAgent,
  withRef,
  Mind,
  Character,
  WithRef,
  supportsAutomaticCaptain,
  type API_PERSONA_VERSION,
  type PersonaVersion,
} from "@um/contracts";
import { logException } from "@um/ui";
import { toast } from "sonner";
import { sendConversionEvent } from "@/services/social";

/**
 * The persona versions the app writes itself, for the triggers to draw.
 * Version 3 is made only by the `create` API: the triggers never process it,
 * so a version-3 persona written here would wait for its pictures forever.
 */
export type TriggerPersonaVersion = Exclude<
  PersonaVersion,
  typeof API_PERSONA_VERSION
>;

interface CharactersState {
  // For list view
  characters: (WithRef<Animal> & { id: string })[];
  loading: boolean;
  error: string | null;
  currentAccountId: string | null;
  unsubscribeSnapshot: Unsubscribe | null;
  subscribeToCharacters: (
    accountId: string,
    mindRef?: DocumentReference<Mind>
  ) => void;
  unsubscribeFromCharacters: () => void;
  hasCharacters: boolean;
  checkHasCharacters: (
    accountId: string,
    mindRef?: DocumentReference<Mind>
  ) => Promise<boolean>;

  // For unassigned characters (owners only)
  unassignedCharacters: (WithRef<Animal> & { id: string })[];
  unassignedLoading: boolean;
  unassignedError: string | null;
  unsubscribeUnassignedSnapshot: Unsubscribe | null;
  subscribeToUnassignedCharacters: (accountId: string) => void;
  unsubscribeFromUnassignedCharacters: () => void;

  // For detail view
  activeCharacter: WithRef<Animal> | null;
  activeCharacterLoading: boolean;
  activeCharacterError: string | null;
  unsubscribeDetailSnapshot: Unsubscribe | null;
  subscribeToCharacterDetail: (accountId: string, characterId: string) => void;
  unsubscribeFromCharacterDetail: () => void;

  // For public character view (e.g., adoption/sharing)
  publicCharacter: (PublicAnimal & { id: string }) | null;
  publicCharacterLoading: boolean;
  publicCharacterError: string | null;
  fetchPublicCharacter: (characterId: string) => Promise<void>;
  clearPublicCharacter: () => void;

  // Actions for modifying character data
  createNewCharacter: (
    accountId: string,
    initialPersonaData: {
      prompt: string;
      imageFile?: File; // TODO: remove this and just use image. consolidate upload login in mvp and classic
      image?: ImageReference;
      desiredImageVariations?: number;
      version?: TriggerPersonaVersion;
    },
    mindId?: string
  ) => Promise<string | null>; // Returns new character ID or null on failure
  addPersonaToCharacter: (
    accountId: string,
    characterId: string,
    personaData: {
      prompt: string;
      promptSummary?: string;
      sourcePersonaId?: string;
      imageFile?: File;
      version?: TriggerPersonaVersion;
    }
  ) => Promise<string | null>; // Returns new persona ID or null
  setDefaultPersona: (
    accountId: string,
    characterId: string,
    personaId: string
  ) => Promise<void>;
  updatePersonaName: (
    accountId: string,
    characterId: string,
    personaId: string,
    newName: string
  ) => Promise<void>;
  updatePersonaPersonalityHints: (
    accountId: string,
    characterId: string,
    personaId: string,
    hints: {
      personalityHintsContext?: string | null;
      personalityHintsFinalized?: boolean;
      personalityHintsSkipped?: boolean;
      personalityHintsApplied?: boolean;
      personalityHintsPendingRefresh?: boolean;
    }
  ) => Promise<void>;
  setPersonaImage: (
    accountId: string,
    characterId: string,
    personaId: string,
    image: ImageReference
  ) => Promise<void>;
  deletePersona: (
    accountId: string,
    characterId: string,
    personaId: string
  ) => Promise<void>;
  /**
   * RAW soft-delete: sets `isDeleted: true` and `isHidden: true` on the Animal
   * document with a direct client write. Does NOT pause the character's jobs —
   * prefer `deleteCharacterSafely` for any user-facing delete. Kept for the
   * character-creation content-block restart, where the character has no jobs
   * yet and a callable round-trip mid-flow isn't worth it.
   */
  deleteCharacter: (accountId: string, characterId: string) => Promise<void>;
  /**
   * Safe delete via the `deleteCharacter` backend callable: pauses all
   * active/review jobs assigned to the character (marked
   * `pausedReason: "character-deleted"` so the UI requires reassignment before
   * resume), then soft-deletes it. Crew/captain cleanup happens in the
   * onAnimalWritten trigger. Returns the paused job ids + whether the deleted
   * character was the captain, for consequence-aware toasts.
   */
  deleteCharacterSafely: (
    accountId: string,
    characterId: string,
    reason?: AnimalDeleteReason,
    /** The retiring mind (browsing context) — enables partial retire for
     *  characters shared across minds; see DeleteCharacterRequest.mindId. */
    mindId?: string
  ) => Promise<DeleteCharacterResponse>;

  // Retired Characters — soft-deleted characters the user can restore
  retiredCharacters: (WithRef<Animal> & { id: string })[];
  retiredLoading: boolean;
  unsubscribeRetiredSnapshot: Unsubscribe | null;
  /**
   * `mindRef` scopes the query server-side (used for member views so the
   * client never receives sibling-profile data). Owners subscribe
   * account-wide and filter client-side by mind — one listener covers the
   * selected mind AND unassigned retired characters with no resubscribe
   * churn when the mind dropdown changes.
   */
  subscribeToRetiredCharacters: (
    accountId: string,
    mindRef?: DocumentReference<Mind>
  ) => void;
  unsubscribeFromRetiredCharacters: () => void;
  /**
   * Bring back: clears `isDeleted`/`isHidden` so the character reappears in the
   * roster (mirrors the admin portal's "Undelete animal"). Deliberately does
   * NOT restore crew roles or resume jobs — the character comes back with a
   * clean slate.
   */
  restoreCharacter: (accountId: string, characterId: string) => Promise<void>;

  // Getter utilities
  getLastCreatedCharacter: (
    accountId: string,
    mindRef?: DocumentReference<Mind>
  ) => Promise<Character | null>;

  // Support crew (Mind.crew)
  supportCrew: CrewMember[];
  supportCrewLoading: boolean;
  supportCrewError: string | null;
  unsubscribeCrewSnapshot: Unsubscribe | null;
  subscribeToSupportCrew: (accountId: string, mindId: string) => void;
  unsubscribeFromSupportCrew: () => void;
  addAnimalToSupportCrew: (
    accountId: string,
    mindId: string,
    animalId: string,
    role?: string,
    personaId?: string
  ) => Promise<void>;
  handleSetCaptain: (
    accountId: string,
    mindId: string,
    characterId: string
  ) => Promise<void>;
  /**
   * For user/anonymous accounts, ensures a mind has a captain. If `mind.defaultAnimal` is
   * currently null, sets it to the given character (mirroring `handleSetCaptain`).
   * If it's already set, this is a no-op. Used during V2 onboarding so the very
   * first character a user creates is automatically promoted to captain
   * without requiring the (now-deprecated) electCaptain vote ceremony.
   */
  ensureMindCaptain: (
    accountId: string,
    mindId: string,
    characterId: string
  ) => Promise<void>;
  removeAnimalFromSupportCrew: (
    accountId: string,
    mindId: string,
    animalId: string
  ) => Promise<void>;

  /** Owner: assign an animal document to a mind (writes Animal.mind). */
  assignAnimalToMind: (
    accountId: string,
    animalId: string,
    mindRef: DocumentReference<Mind>
  ) => Promise<void>;
}

// Removed mapping; list consumers will use persona helpers directly from WithRef<Animal>

// Internal helper for image upload
const uploadCharacterImage = async (
  accountId: string,
  characterId: string,
  personaId: string,
  imageFile: File
): Promise<ImageReference | null> => {
  try {
    return await uploadAccountImage(accountId, imageFile);
  } catch (error) {
    logException(error);
    toast.error("Error uploading image. Please try again.");
    throw error;
  }
};

export const useCharactersStore = create<CharactersState>((set, get) => ({
  // List view state and actions
  characters: [],
  loading: true,
  error: null,
  currentAccountId: null,
  unsubscribeSnapshot: null,
  hasCharacters: false,

  subscribeToCharacters: (
    accountId: string,
    mindRef?: DocumentReference<Mind>
  ) => {
    if (!accountId) {
      set({
        characters: [],
        loading: false,
        error: "Account ID needed for subscription.",
        currentAccountId: null,
        unsubscribeSnapshot: null,
      });
      return;
    }
    const existingUnsubscribe = get().unsubscribeSnapshot;
    if (existingUnsubscribe) existingUnsubscribe();

    // Clear the previous scope's characters so a resubscribe (e.g. switching
    // Owner view ↔ a mind view) never renders the old list while the new
    // snapshot is in flight.
    set({
      characters: [],
      loading: true,
      error: null,
      currentAccountId: accountId,
    });
    const charactersColRef = collection(db, "Account", accountId, "Animal");
    // Scope visibility: the characters slot is shared by several routes and a
    // wrong-scope subscribe stomping a mind-scoped one is invisible without
    // this line. Keep it — it's the first thing to check when "filtering by
    // Mind stopped working".
    console.log(
      `[useCharactersStore] subscribeToCharacters scope: ${mindRef ? `mind ${mindRef.id}` : "account-wide"}`
    );
    const charactersQuery = query(
      charactersColRef,
      where("isHidden", "==", false),
      ...(mindRef ? [where("mind", "==", mindRef)] : []),
      orderBy("created", "desc")
    );
    const unsubscribe = onSnapshot(
      charactersQuery,
      (snapshot) => {
        const fetchedCharacters = snapshot.docs.map((d) => ({
          id: d.id,
          ...withRef<Animal>(d as unknown as DocumentSnapshot<Animal>),
        }));
        set({ characters: fetchedCharacters, loading: false, error: null });
      },
      (err) => {
        console.error("Error in onSnapshot listener (list):", err);
        set({
          error: "Failed to load characters in real-time.",
          loading: false,
        });
      }
    );
    set({ unsubscribeSnapshot: unsubscribe });
  },

  unsubscribeFromCharacters: () => {
    const unsubscribe = get().unsubscribeSnapshot;
    if (unsubscribe) unsubscribe();
    set({
      characters: [],
      loading: false,
      error: null,
      currentAccountId: null,
      unsubscribeSnapshot: null,
      hasCharacters: false,
    });
  },

  // Efficient existence check for visible characters
  // Excludes hidden characters (isHidden != true) to match user expectations
  checkHasCharacters: async (
    accountId: string,
    mindRef?: DocumentReference<Mind>
  ) => {
    try {
      if (!accountId) {
        console.log("[checkHasCharacters] No accountId provided");
        set({ hasCharacters: false });
        return false;
      }

      console.log("[checkHasCharacters] Checking for accountId:", accountId);
      const charactersColRef = collection(db, "Account", accountId, "Animal");

      // First, get ALL documents to see what exists (for debugging)
      const allDocsQuery = query(
        charactersColRef,
        ...(mindRef ? [where("mind", "==", mindRef)] : []),
        limit(10)
      );
      const allSnapshot = await getDocs(allDocsQuery);

      console.log("[checkHasCharacters] All documents in collection:", {
        accountId,
        count: allSnapshot.size,
        docs: allSnapshot.docs.map((d) => ({
          id: d.id,
          isHidden: d.data().isHidden,
          isDeleted: d.data().isDeleted,
        })),
      });

      // Filter to only visible characters (not hidden)
      // A character is visible if isHidden is falsy (false, undefined, null)
      const visibleDocs = allSnapshot.docs.filter((d) => !d.data().isHidden);
      const hasAny = visibleDocs.length > 0;

      console.log("[checkHasCharacters] Result:", {
        accountId,
        hasAny,
        visibleCount: visibleDocs.length,
        visibleIds: visibleDocs.map((d) => d.id),
      });

      set({ hasCharacters: hasAny });
      return hasAny;
    } catch (error) {
      // Log the full error for debugging
      console.error("[checkHasCharacters] Error checking characters:", error);
      set({ hasCharacters: false });
      return false;
    }
  },

  // Getter utilities
  getLastCreatedCharacter: async (
    accountId: string,
    mindRef?: DocumentReference<Mind>
  ) => {
    const charactersColRef = collection(db, "Account", accountId, "Animal");
    const charactersQuery = query(
      charactersColRef,
      where("isHidden", "==", false),
      ...(mindRef ? [where("mind", "==", mindRef)] : []),
      orderBy("created", "desc"),
      limit(1)
    );

    const snapshot = await getDocs(charactersQuery);
    const characters = snapshot.docs.map((d) => ({
      id: d.id,
      ...withRef<Animal>(d as unknown as DocumentSnapshot<Animal>),
    }));
    return characters[0] || null;
  },

  // Support crew (Mind.crew)
  supportCrew: [],
  supportCrewLoading: false,
  supportCrewError: null,
  unsubscribeCrewSnapshot: null,
  subscribeToSupportCrew: (accountId: string, mindId: string) => {
    if (!accountId || !mindId) {
      set({
        supportCrew: [],
        supportCrewLoading: false,
        supportCrewError: "Account and Mind ID required for crew subscription.",
        unsubscribeCrewSnapshot: null,
      });
      return;
    }

    const existing = get().unsubscribeCrewSnapshot;
    if (existing) existing();

    set({ supportCrewLoading: true, supportCrewError: null });
    const mindRef = doc(
      db,
      "Account",
      accountId,
      "Mind",
      mindId
    ) as DocumentReference<Mind>;
    const unsubscribe = onSnapshot(
      mindRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const crew = (data.crew || []) as CrewMember[];
          set({
            supportCrew: crew,
            supportCrewLoading: false,
            supportCrewError: null,
          });
        } else {
          set({
            supportCrew: [],
            supportCrewLoading: false,
            supportCrewError: null,
          });
        }
      },
      (err) => {
        console.error("Error in onSnapshot listener (crew):", err);
        set({
          supportCrewError: "Failed to load support crew in real-time.",
          supportCrewLoading: false,
        });
      }
    );
    set({ unsubscribeCrewSnapshot: unsubscribe });
  },
  unsubscribeFromSupportCrew: () => {
    const unsubscribe = get().unsubscribeCrewSnapshot;
    if (unsubscribe) unsubscribe();
    set({
      supportCrew: [],
      supportCrewLoading: false,
      supportCrewError: null,
      unsubscribeCrewSnapshot: null,
    });
  },

  addAnimalToSupportCrew: async (
    accountId,
    mindId,
    animalId,
    role = "supporter",
    personaId
  ) => {
    if (!accountId || !mindId || !animalId) {
      console.error(
        "addAnimalToSupportCrew: accountId, mindId, and animalId are required"
      );
      return;
    }
    try {
      const mindRef = doc(
        db,
        "Account",
        accountId,
        "Mind",
        mindId
      ) as DocumentReference<Mind>;
      const animalRef = doc(
        db,
        "Account",
        accountId,
        "Animal",
        animalId
      ) as DocumentReference<Animal>;

      // Fetch animal to construct an Agent with name/image using existing helper
      const animalSnap = await getDoc(animalRef);
      if (!animalSnap.exists()) {
        console.error("addAnimalToSupportCrew: animal not found", {
          accountId,
          animalId,
        });
        return;
      }

      const mindSnap = await getDoc(mindRef);
      if (!mindSnap.exists()) {
        console.error("addAnimalToSupportCrew: mind not found", {
          accountId,
          mindId,
        });
        return;
      }

      const existingCrew = mindSnap.data()?.crew || [];
      const alreadyOnCrew = existingCrew.some((cm) => {
        const aid = (cm?.agent as AnimalAgent)?.animal?.id as
          | string
          | undefined;
        return aid === animalId;
      });
      if (alreadyOnCrew) {
        return;
      }

      const animalWithRef = withRef<Animal>(
        animalSnap as unknown as DocumentSnapshot<Animal>
      );
      const agent = getAgent(animalWithRef, personaId);

      const newMember: CrewMember = {
        role,
        agent: agent,
      };

      await updateDoc(mindRef, {
        crew: arrayUnion(newMember),
        updated: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error adding animal to support crew:", error);
    }
  },

  handleSetCaptain: async (accountId, mindId, characterId) => {
    if (!accountId || !mindId || !characterId) {
      console.error(
        "handleSetCaptain: accountId, mindId, and characterId are required"
      );
      return;
    }
    try {
      sendConversionEvent("um_accept_daily_loop");

      const mindRef = doc(
        db,
        "Account",
        accountId,
        "Mind",
        mindId
      ) as DocumentReference<Mind>;
      const defaultAnimalRef = doc(
        db,
        "Account",
        accountId,
        "Animal",
        characterId
      );

      let mindSnap = await getDoc(mindRef);
      if (!mindSnap.exists()) {
        console.error("handleSetCaptain: mind not found", {
          accountId,
          mindId,
        });
        return;
      }

      let currentCrew = mindSnap.data()?.crew || [];
      const alreadyOnCrew = currentCrew.some(
        (cm) =>
          ((cm?.agent as AnimalAgent)?.animal?.id as string | undefined) ===
          characterId,
      );
      if (!alreadyOnCrew) {
        await get().addAnimalToSupportCrew(accountId, mindId, characterId, "");
        mindSnap = await getDoc(mindRef);
        if (!mindSnap.exists()) {
          console.error("handleSetCaptain: mind not found after crew add", {
            accountId,
            mindId,
          });
          return;
        }
        currentCrew = mindSnap.data()?.crew || [];
      }

      const updatedCrew = currentCrew.map((cm) => {
        const animalId = (cm?.agent as AnimalAgent).animal?.id as
          | string
          | undefined;
        if (!animalId) return cm;
        if (animalId === characterId) {
          return { ...cm, role: "Captain Elect" };
        }
        return cm;
      });

      await updateDoc(mindRef, {
        crew: updatedCrew,
        defaultAnimal: defaultAnimalRef,
        captainSelectionRequestedAt: serverTimestamp(),
        updated: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error setting captain:", error);
    }
  },

  ensureMindCaptain: async (accountId, mindId, characterId) => {
    if (!accountId || !mindId || !characterId) {
      console.error(
        "ensureMindCaptain: accountId, mindId, and characterId are required"
      );
      return;
    }
    try {
      const accountSnap = await getDoc(doc(db, "Account", accountId));
      if (!accountSnap.exists() || !supportsAutomaticCaptain(accountSnap.data())) return;
      const mindRef = doc(
        db,
        "Account",
        accountId,
        "Mind",
        mindId
      ) as DocumentReference<Mind>;

      const mindSnap = await getDoc(mindRef);
      if (!mindSnap.exists()) {
        console.warn("ensureMindCaptain: mind not found", { accountId, mindId });
        return;
      }

      // Already has a captain — nothing to do. Captain swaps go through
      // handleSetCaptain explicitly.
      const currentDefault = mindSnap.data()?.defaultAnimal;
      if (currentDefault) {
        return;
      }

      console.log(
        "[ensureMindCaptain] auto-assigning first character as captain",
        { mindId, characterId }
      );
      // Reuse handleSetCaptain for the actual write so crew bookkeeping and
      // analytics stay consistent.
      await get().handleSetCaptain(accountId, mindId, characterId);
    } catch (error) {
      console.error("Error ensuring mind captain:", error);
    }
  },

  removeAnimalFromSupportCrew: async (accountId, mindId, animalId) => {
    if (!accountId || !mindId || !animalId) {
      console.error(
        "removeAnimalFromSupportCrew: accountId, mindId, and animalId are required"
      );
      return;
    }
    try {
      const mindRef = doc(
        db,
        "Account",
        accountId,
        "Mind",
        mindId
      ) as DocumentReference<Mind>;

      const mindSnap = await getDoc(mindRef);
      if (!mindSnap.exists()) {
        console.error("removeAnimalFromSupportCrew: mind not found", {
          accountId,
          mindId,
        });
        return;
      }

      const data = mindSnap.data();
      const currentCrew = data?.crew || [];
      const filteredCrew = currentCrew.filter(
        (cm) =>
          ((cm?.agent as AnimalAgent).animal?.id as string | undefined) !==
          animalId
      );
      const removedWasCaptain = currentCrew.some(
        (cm) =>
          (cm?.agent as AnimalAgent).animal?.id === animalId &&
          typeof cm?.role === "string" &&
          cm.role.toLowerCase() === "captain"
      );

      await updateDoc(mindRef, {
        crew: filteredCrew,
        ...(removedWasCaptain ? { defaultAnimal: null } : {}),
        updated: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error removing animal from support crew:", error);
    }
  },

  assignAnimalToMind: async (accountId, animalId, mindRef) => {
    if (!accountId || !animalId || !mindRef) {
      console.error(
        "assignAnimalToMind: accountId, animalId, and mindRef are required"
      );
      return;
    }
    try {
      const animalRef = doc(
        db,
        "Account",
        accountId,
        "Animal",
        animalId
      ) as DocumentReference<Animal>;
      await updateDoc(animalRef, {
        mind: mindRef,
        updated: serverTimestamp(),
      });
    } catch (error) {
      console.error("assignAnimalToMind failed:", error);
      throw error;
    }
  },

  // Unassigned characters state and actions
  unassignedCharacters: [],
  unassignedLoading: true,
  unassignedError: null,
  unsubscribeUnassignedSnapshot: null,

  subscribeToUnassignedCharacters: (accountId: string) => {
    if (!accountId) {
      set({
        unassignedCharacters: [],
        unassignedLoading: false,
        unassignedError:
          "Account ID needed for unassigned characters subscription.",
        unsubscribeUnassignedSnapshot: null,
      });
      return;
    }
    const existingUnsubscribe = get().unsubscribeUnassignedSnapshot;
    if (existingUnsubscribe) existingUnsubscribe();

    set({ unassignedLoading: true, unassignedError: null });
    const unassignedColRef = collection(db, "Account", accountId, "Animal");

    // Since Firestore doesn't support "field doesn't exist" queries directly,
    // we'll query all characters and filter for those without mind field
    // This is simpler and more reliable than trying complex null queries
    const unassignedQuery = query(
      unassignedColRef,
      where("isHidden", "==", false),
      orderBy("created", "desc")
    );

    const unsubscribe = onSnapshot(
      unassignedQuery,
      (snapshot) => {
        const unassignedCharacters: (WithRef<Animal> & { id: string })[] = [];

        snapshot.docs.forEach((d) => {
          const data = d.data();
          if (data.mind === undefined || data.mind === null) {
            unassignedCharacters.push({
              id: d.id,
              ...withRef<Animal>(d as unknown as DocumentSnapshot<Animal>),
            });
          }
        });

        console.log(
          `Found ${unassignedCharacters.length} unassigned characters out of ${snapshot.docs.length} total`
        );
        set({
          unassignedCharacters,
          unassignedLoading: false,
          unassignedError: null,
        });
      },
      (err) => {
        console.error("Error in onSnapshot listener (unassigned):", err);
        set({
          unassignedError: "Failed to load unassigned characters in real-time.",
          unassignedLoading: false,
        });
      }
    );
    set({ unsubscribeUnassignedSnapshot: unsubscribe });
  },
  unsubscribeFromUnassignedCharacters: () => {
    const unsubscribe = get().unsubscribeUnassignedSnapshot;
    if (unsubscribe) unsubscribe();
    set({
      unassignedCharacters: [],
      unassignedLoading: false,
      unassignedError: null,
      unsubscribeUnassignedSnapshot: null,
    });
  },

  // Detail view state and actions
  activeCharacter: null,
  activeCharacterLoading: true,
  activeCharacterError: null,
  unsubscribeDetailSnapshot: null,

  subscribeToCharacterDetail: (accountId: string, characterId: string) => {
    console.log("[useCharactersStore] subscribeToCharacterDetail called:", {
      accountId,
      characterId,
    });
    if (!accountId || !characterId) {
      console.log(
        "[useCharactersStore] Missing accountId or characterId, returning early"
      );
      set({
        activeCharacter: null,
        activeCharacterLoading: false,
        activeCharacterError: "Account and Character ID required.",
        unsubscribeDetailSnapshot: null,
      });
      return;
    }
    const existingUnsubscribe = get().unsubscribeDetailSnapshot;
    if (existingUnsubscribe) {
      console.log("[useCharactersStore] Unsubscribing existing snapshot");
      existingUnsubscribe();
    }

    set({ activeCharacterLoading: true, activeCharacterError: null });
    const characterPath = `Account/${accountId}/Animal/${characterId}`;
    console.log(
      "[useCharactersStore] Setting up onSnapshot for:",
      characterPath
    );
    const characterRef = doc(
      db,
      "Account",
      accountId,
      "Animal",
      characterId
    ) as DocumentReference<Animal>;
    // onSnapshotWithRetry, NOT raw onSnapshot: a Firestore listener that
    // errors is terminated and never fires again. The character-creation flow
    // hits exactly that — the auth-link sign-in re-authenticates the streams
    // while the new uid is not yet on the account's ACL, the listener gets a
    // permission-denied, and every character-derived visual on the page stays
    // frozen until a reload creates a fresh listener. The retry wrapper
    // resubscribes after errors so the listener survives auth transitions.
    const unsubscribe = onSnapshotWithRetry<Animal>(
      characterRef,
      (charDetail) => {
        console.log("[useCharactersStore] onSnapshot callback:", {
          exists: !!charDetail,
          path: characterPath,
        });
        if (charDetail) {
          console.log("[useCharactersStore] Character loaded:", {
            id: charDetail.ref.id,
            personasCount: charDetail.personas?.length,
          });
          set({
            activeCharacter: charDetail,
            activeCharacterLoading: false,
            activeCharacterError: null,
          });
        } else {
          console.log(
            "[useCharactersStore] Character document does not exist!"
          );
          set({
            activeCharacter: null,
            activeCharacterLoading: false,
            activeCharacterError: "Character not found.",
          });
        }
      },
      (err) => {
        // Transient stream error before the first result — the retry loop is
        // re-subscribing, so keep any last-known character instead of wiping
        // the UI; only surface the error state.
        console.error("[useCharactersStore] onSnapshot error:", err);
        set({
          activeCharacterLoading: false,
          activeCharacterError: "Failed to load character details.",
        });
      }
    );
    set({ unsubscribeDetailSnapshot: unsubscribe });
  },
  unsubscribeFromCharacterDetail: () => {
    const unsubscribe = get().unsubscribeDetailSnapshot;
    if (unsubscribe) unsubscribe();
    set({
      activeCharacter: null,
      activeCharacterLoading: false,
      activeCharacterError: null,
      unsubscribeDetailSnapshot: null,
    });
  },

  // For public character view (e.g., adoption/sharing)
  publicCharacter: null,
  publicCharacterLoading: true,
  publicCharacterError: null,
  fetchPublicCharacter: async (characterId: string) => {
    if (!characterId) {
      set({
        publicCharacter: null,
        publicCharacterLoading: false,
        publicCharacterError:
          "Character ID is required to fetch public character.",
      });
      return;
    }

    set({ publicCharacterLoading: true, publicCharacterError: null });

    try {
      // Fetch from the public collection
      const characterRef = doc(
        db,
        "Account",
        "public",
        "Animal",
        characterId
      ) as DocumentReference<PublicAnimal>;

      const docSnap = await getDoc(characterRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        set({
          publicCharacter: {
            id: docSnap.id,
            ...data,
          } as unknown as PublicAnimal & { id: string },
          publicCharacterLoading: false,
          publicCharacterError: null,
        });
      } else {
        set({
          publicCharacter: null,
          publicCharacterLoading: false,
          publicCharacterError: "Character not found.",
        });
      }
    } catch (err) {
      logException(err);
      console.error("Error fetching public character:", err);
      set({
        publicCharacter: null,
        publicCharacterLoading: false,
        publicCharacterError: "Failed to load character details.",
      });
    }
  },
  clearPublicCharacter: () => {
    set({
      publicCharacter: null,
      publicCharacterLoading: true,
      publicCharacterError: null,
    });
  },

  // Actions for modifying character data
  createNewCharacter: async (accountId, initialPersonaData, mindId) => {
    if (!accountId) {
      console.error("createNewCharacter: accountId is required");
      return null;
    }
    const mindRef = mindId
      ? (doc(
          db,
          "Account",
          accountId,
          "Mind",
          mindId
        ) as DocumentReference<Mind>)
      : undefined;
    set({ activeCharacterLoading: true }); // Potentially use a different loading state for actions
    try {
      const defaultPersonaId = newId(); // Generate a unique ID for the default persona
      let uploadedImageRef: ImageReference | null = null;

      // A temporary character ID for image upload path. This won't be the final document ID.
      const tempCharacterIdForUpload = newId();

      if (initialPersonaData.imageFile) {
        uploadedImageRef = await uploadCharacterImage(
          accountId,
          tempCharacterIdForUpload, // Use a temporary ID for upload path
          defaultPersonaId, // Use the actual persona ID for image path context
          initialPersonaData.imageFile
        );
      }
      const personaImage: ImageReference | undefined =
        uploadedImageRef ?? initialPersonaData.image ?? undefined;

      const defaultPersona: Persona = {
        id: defaultPersonaId,
        sourcePrompt: initialPersonaData.prompt.trim(),
        sourceImage: personaImage,
        desiredImageVariations: initialPersonaData.desiredImageVariations,
        version: initialPersonaData.version ?? 1,
      };

      console.log("[useCharactersStore] Creating persona:", {
        id: defaultPersonaId,
        sourcePrompt: defaultPersona.sourcePrompt,
        hasSourceImage: !!defaultPersona.sourceImage,
        sourceImageId: defaultPersona.sourceImage?.id,
      });

      const charactersCollectionRef = collection(
        db,
        "Account",
        accountId,
        "Animal"
      );

      const newCharacterData: WithFieldValue<Animal> & { personas: Persona[] } =
        {
          personas: [defaultPersona],
          isDeleted: false,
          isHidden: false,
          created: serverTimestamp(),
          updated: serverTimestamp(),
          mind: mindRef,
          // name, description, avatarUrl will be derived by client code when rendering
        };

      // Use addDoc to let Firestore generate the document ID
      const docRef = await addDoc(charactersCollectionRef, newCharacterData);
      console.log("[useCharactersStore] Character created:", docRef.id);

      set({ activeCharacterLoading: false });
      return docRef.id; // Return the auto-generated character ID
    } catch (error) {
      logException(error);
      console.error("Error creating new character:", error);
      set({
        activeCharacterLoading: false,
        activeCharacterError: "Failed to create character.",
      });
      return null;
    }
  },

  // Placeholder for addPersonaToCharacter
  addPersonaToCharacter: async (accountId, characterId, personaData) => {
    if (!accountId || !characterId) {
      console.error(
        "addPersonaToCharacter: accountId and characterId are required"
      );
      return null;
    }
    // set({ activeCharacterLoading: true }); // Or a specific persona adding loading state
    try {
      const newPersonaId = newId();
      let uploadedImageRef: ImageReference | null = null;
      if (personaData.imageFile) {
        uploadedImageRef = await uploadCharacterImage(
          accountId,
          characterId,
          newPersonaId, // Use new persona ID for image path context
          personaData.imageFile
        );
        // No explicit error throw here, null will be handled by assignment
      }
      const personaImage: ImageReference | undefined =
        uploadedImageRef ?? undefined;

      await editPersonaFn({
        accountId,
        characterId,
        personaId: newPersonaId,
        action: "add",
        patch: Object.fromEntries(Object.entries({
          sourcePrompt: personaData.prompt.trim(),
          promptSummary: personaData.promptSummary,
          sourcePersonaId: personaData.sourcePersonaId,
          image: personaImage,
          version: personaData.version ?? 1,
        }).filter(([, value]) => value !== undefined)),
      });
      return newPersonaId;
    } catch (error) {
      logException(error);
      console.error("Error adding persona:", error);
      throw error;
    }
  },

  setDefaultPersona: async (accountId, characterId, personaId) => {
    await changeOutfitFn({
      accountId, characterId, personaId, requestId: crypto.randomUUID(),
    });
  },

  updatePersonaName: async (accountId, characterId, personaId, newName) => {
    await editPersonaFn({
      accountId, characterId, personaId, action: "update", patch: { name: newName },
    });
  },

  updatePersonaPersonalityHints: async (accountId, characterId, personaId, hints) => {
    const patch = Object.fromEntries(Object.entries(hints).filter(([, value]) => value !== undefined));
    await editPersonaFn({ accountId, characterId, personaId, action: "update", patch });
  },

  setPersonaImage: async (accountId, characterId, personaId, image) => {
    await editPersonaFn({
      accountId, characterId, personaId, action: "update", patch: { image },
    });
  },

  deletePersona: async (accountId, characterId, personaId) => {
    const animal = get().activeCharacter;
    if (animal?.defaultPersonaId === personaId) {
      const next = [...animal.personas].reverse().find(
        p => p.id !== personaId && !p.isDeleted && !p.wornItems?.length && !p.equippedItems
      );
      if (!next) throw new Error("Create another picture before deleting this version.");
      await changeOutfitFn({
        accountId, characterId, personaId: next.id, requestId: crypto.randomUUID(),
      });
    }
    await editPersonaFn({ accountId, characterId, personaId, action: "delete" });
  },

  deleteCharacter: async (accountId, characterId) => {
    if (!accountId || !characterId) {
      console.error("deleteCharacter: accountId and characterId are required");
      return;
    }
    try {
      console.log("[useCharactersStore] Soft-deleting character:", {
        accountId,
        characterId,
      });
      const characterRef = doc(
        db,
        "Account",
        accountId,
        "Animal",
        characterId
      ) as DocumentReference<Animal>;
      await updateDoc(characterRef, {
        isDeleted: true,
        // List queries filter on isHidden == false, so a deleted character
        // must also be hidden to disappear from the characters grid.
        isHidden: true,
        updated: serverTimestamp(),
      });
      console.log("[useCharactersStore] Character soft-deleted successfully");
    } catch (error) {
      logException(error);
      console.error("Error deleting character:", error);
      throw error;
    }
  },

  deleteCharacterSafely: async (accountId, characterId, reason, mindId) => {
    if (!accountId || !characterId) {
      throw new Error(
        "deleteCharacterSafely: accountId and characterId are required"
      );
    }
    try {
      const result = await deleteCharacterFn({
        accountId,
        animalId: characterId,
        ...(reason ? { reason } : {}),
        ...(mindId ? { mindId } : {}),
      });
      console.log("[useCharactersStore] Character safely deleted:", result.data);
      return result.data;
    } catch (error) {
      logException(error);
      console.error("Error safely deleting character:", error);
      throw error;
    }
  },

  // ─── Retired Characters (soft-deleted characters) ────────────────────────

  retiredCharacters: [],
  retiredLoading: false,
  unsubscribeRetiredSnapshot: null,

  subscribeToRetiredCharacters: (accountId, mindRef) => {
    if (!accountId) return;
    get().unsubscribeRetiredSnapshot?.();

    set({ retiredCharacters: [], retiredLoading: true });
    const retiredQuery = query(
      collection(db, "Account", accountId, "Animal"),
      where("isDeleted", "==", true),
      // Only user-retired characters are restorable. Reported/safety
      // removals and legacy soft-deletes (no deletedReason) never surface.
      where("deletedReason", "==", USER_RETIRED_DELETE_REASON),
      ...(mindRef ? [where("mind", "==", mindRef)] : [])
    ) as Query<Animal>;
    const unsubscribe = onSnapshotWithRetry<Animal>(
      retiredQuery,
      (result) => {
        const docs = (Array.isArray(result) ? result : []) as WithRef<Animal>[];
        const retired = docs
          .map((d) => ({ ...d, id: d.ref.id }))
          // Newest removals first (no orderBy in the query — a composite
          // index isn't worth it for this small list).
          .sort(
            (a, b) =>
              (b.updated?.toMillis?.() ?? 0) - (a.updated?.toMillis?.() ?? 0)
          );
        set({ retiredCharacters: retired, retiredLoading: false });
      },
      (error) => {
        logException(error);
        console.error("Error subscribing to retired characters:", error);
        set({ retiredCharacters: [], retiredLoading: false });
      }
    );
    set({ unsubscribeRetiredSnapshot: unsubscribe });
  },

  unsubscribeFromRetiredCharacters: () => {
    get().unsubscribeRetiredSnapshot?.();
    set({
      unsubscribeRetiredSnapshot: null,
      retiredCharacters: [],
      retiredLoading: false,
    });
  },

  restoreCharacter: async (accountId, characterId) => {
    if (!accountId || !characterId) {
      throw new Error("restoreCharacter: accountId and characterId are required");
    }
    try {
      const characterRef = doc(
        db,
        "Account",
        accountId,
        "Animal",
        characterId
      ) as DocumentReference<Animal>;
      await updateDoc(characterRef, {
        isDeleted: false,
        isHidden: false,
        deletedReason: deleteField(),
        updated: serverTimestamp(),
      });

      // Clear the character-deleted pause marker on this character's jobs
      // (marker only — foreign markers like subscription-lapsed belong to
      // their own resume flows). Jobs stay PAUSED — restore never resumes
      // jobs — but without the stale marker they resume normally from the
      // job dialog instead of demanding a reassignment for a character
      // that's alive again. Best-effort: a failure here must not fail the
      // restore itself.
      try {
        const pausedJobsSnap = await getDocs(
          query(
            collection(db, "Account", accountId, "Job"),
            where("agent.animal", "==", characterRef),
            where("status", "==", "paused")
          )
        );
        await Promise.all(
          pausedJobsSnap.docs
            .filter(
              (d) => d.data().pausedReason === CHARACTER_DELETED_PAUSE_REASON
            )
            .map((d) => updateDoc(d.ref, { pausedReason: deleteField() }))
        );
      } catch (jobError) {
        logException(jobError);
        console.error("Error clearing pause markers on restore:", jobError);
      }

      console.log("[useCharactersStore] Character restored:", characterId);
    } catch (error) {
      logException(error);
      console.error("Error restoring character:", error);
      throw error;
    }
  },
}));
